import { useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { ApiFailure, api } from "../api/client";
import type { Balance, CallerId, ErasureResult, PhoneNumber, RateDeck, RoleKey, Tenant, TenantUserCreated, Trunk } from "../api/types";
import { failureMessage, useResource } from "../api/useResource";
import { usePagedResource } from "../api/usePagedResource";
import {
  ActionError, Badge, Button, Field, Modal, PageHeader, inputClass,
} from "../components/ui";
import { DataTable } from "../components/data-table";
import type { DataTableRowAction } from "../components/data-table";
import { useSession } from "../store/session";
import { formatDateTime } from "./Calls";

// Cuentas de la plataforma. Vista de OMITECH (superadmin).
//
// Aquí viven las tres palancas que paran una cuenta, y son distintas a
// propósito:
//
//   - **Kill switch**: corta la originación al instante y deja todo lo demás
//     en pie. Es la palanca de fraude, reversible en un clic.
//   - **Suspender**: cambia el estado de la cuenta y cuelga lo que esté en
//     curso. Es la palanca de impago.
//   - **Borrar datos**: destruye CDR y metadatos de stream. No es reversible y
//     no se usa por mora; es para una solicitud de borrado.

export function Tenants() {
  const tenants = usePagedResource<Tenant>("/api/v1/tenants");
  const can = useSession((s) => s.can);
  const reloadSession = useSession((s) => s.load);

  // Los decks solo se piden si se pueden asignar: `rates.manage` es un permiso
  // distinto de `tenants.manage` (internal/api/router.go:248).
  const canAssignDeck = can("rates.manage");
  const decks = useResource<RateDeck[]>(canAssignDeck ? "/api/v1/rate-decks" : null);

  // La troncal decide por dónde sale el tráfico de la cuenta, y sin ella no
  // puede marcar: el portal mostraba «no tiene troncal asignada» sin ofrecer
  // forma de asignarla, y la única salida era tocar la base.
  const canAssignTrunk = can("trunks.manage");
  const trunks = useResource<Trunk[]>(canAssignTrunk ? "/api/v1/trunks" : null);

  const [creating, setCreating] = useState(false);
  const [dandoUsuario, setDandoUsuario] = useState<Tenant | null>(null);
  const [dandoCallerID, setDandoCallerID] = useState<Tenant | null>(null);
  const [dandoNumeros, setDandoNumeros] = useState<Tenant | null>(null);
  const [abonando, setAbonando] = useState<Tenant | null>(null);
  const [erasing, setErasing] = useState<Tenant | null>(null);
  const [suspending, setSuspending] = useState<Tenant | null>(null);
  const [asignandoTroncal, setAsignandoTroncal] = useState<Tenant | null>(null);
  const [asignandoDeck, setAsignandoDeck] = useState<Tenant | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(tenant: Tenant, run: () => Promise<unknown>) {
    setActionError(null);
    setBusyId(tenant.id);
    try {
      await run();
      await tenants.reload(true);
      // La lista de cuentas de la sesión alimenta el conmutador del encabezado:
      // si cambió un nombre o se suspendió una cuenta, hay que releerla o el
      // conmutador sigue mostrando el estado anterior.
      await reloadSession();
    } catch (err) {
      setActionError(failureMessage(err, "no se pudo aplicar el cambio"));
    } finally {
      setBusyId(null);
    }
  }

  // Las columnas se declaran aquí, en la pantalla, y no dentro del
  // DataTable: qué es un «kill switch» o cómo se pinta una cuenta suspendida
  // es conocimiento de Cuentas, y meterlo en el componente genérico lo ataría
  // a este módulo.
  const columnas = useMemo<ColumnDef<Tenant, any>[]>(
    () => [
      {
        id: "name",
        accessorFn: (tenant) => `${tenant.name} ${tenant.slug}`,
        header: "Cuenta",
        meta: { label: "Cuenta" },
        // Se ordena y se busca por nombre y slug juntos, que es como la
        // gente busca una cuenta: a veces se acuerda de uno y a veces del
        // otro.
        cell: ({ row }) => {
          const tenant = row.original;
          return (
            <>
              <div className="font-medium text-slate-100">{tenant.name}</div>
              <div className="text-xs text-slate-500">
                <code>{tenant.slug}</code> · <span className="font-mono">{tenant.id}</span>
              </div>
              {tenant.suspended_reason !== undefined && tenant.suspended_reason !== null && (
                <div className="mt-1 text-xs text-amber-300">Motivo: {tenant.suspended_reason}</div>
              )}
            </>
          );
        },
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado", width: "8rem" },
        cell: ({ row }) => (
          <Badge tone={TENANT_STATUS_TONES[row.original.status]}>
            {TENANT_STATUS_LABELS[row.original.status]}
          </Badge>
        ),
      },
      {
        id: "kill_switch",
        accessorFn: (tenant) => String(tenant.kill_switch),
        header: "Originación",
        meta: { label: "Originación", width: "9rem" },
        cell: ({ row }) =>
          row.original.kill_switch ? (
            <Badge tone="bad">Cortada</Badge>
          ) : (
            <Badge tone="good">Permitida</Badge>
          ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Creada",
        // El identificador y la fecha no merecen una línea en un móvil de
        // 360 px: se ocultan ahí y se pueden ocultar también en escritorio.
        meta: { label: "Creada", width: "12rem", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.created_at)}</span>
        ),
      },
    ],
    [],
  );

  // Las ocho acciones de una cuenta. Viven aquí con sus permisos y sus
  // confirmaciones: el DataTable solo sabe pintarlas en un menú.
  const acciones = useMemo<DataTableRowAction<Tenant>[]>(
    () => [
      {
        label: "Añadir usuario",
        // Sin esto, una cuenta recién creada no tiene forma de tener su
        // primer usuario: `POST /users` invita a la cuenta activa, y un
        // superadministrador no pertenece a ninguna.
        onSelect: (tenant) => setDandoUsuario(tenant),
      },
      {
        label: "Añadir caller ID",
        onSelect: (tenant) => setDandoCallerID(tenant),
      },
      { label: "Números", onSelect: (tenant) => setDandoNumeros(tenant) },
      {
        // Con el cobro prepago, una cuenta sin saldo no origina ni una
        // llamada: responde 402.
        label: "Abonar saldo",
        onSelect: (tenant) => setAbonando(tenant),
      },
      {
        label: "Asignar troncal de salida",
        hidden: () => !canAssignTrunk,
        disabled: () =>
          (trunks.data ?? []).some((t) => t.status === "active" && t.direction !== "inbound")
            ? false
            : "No hay troncales de salida activas",
        onSelect: (tenant) => setAsignandoTroncal(tenant),
      },
      {
        label: "Asignar tarifario",
        hidden: () => !canAssignDeck,
        onSelect: (tenant) => setAsignandoDeck(tenant),
      },
      {
        label: "Kill switch",
        tone: "danger",
        onSelect: (tenant) =>
          void act(tenant, () =>
            api.patch(`/api/v1/tenants/${tenant.id}`, { kill_switch: !tenant.kill_switch }),
          ),
      },
      {
        label: "Suspender",
        tone: "danger",
        hidden: (tenant) => tenant.status === "suspended",
        onSelect: (tenant) => setSuspending(tenant),
      },
      {
        label: "Reanudar",
        hidden: (tenant) => tenant.status !== "suspended",
        onSelect: (tenant) =>
          void act(tenant, () => api.post(`/api/v1/tenants/${tenant.id}/resume`)),
      },
      { label: "Borrar datos", tone: "danger", onSelect: (tenant) => setErasing(tenant) },
    ],
    [canAssignTrunk, canAssignDeck, trunks.data],
  );

  return (
    <div>
      <PageHeader
        title="Cuentas"
        description="Los tenants de la plataforma. El kill switch corta la originación al instante y es reversible; suspender cuelga lo que esté en curso."
        action={!creating && <Button onClick={() => setCreating(true)}>Nueva cuenta</Button>}
      />

      {actionError !== null && <ActionError message={actionError} />}

      {creating && (
        <CreateTenantForm
          onCancel={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void tenants.reload(true);
            void reloadSession();
          }}
        />
      )}

      {suspending !== null && (
        <SuspendForm
          tenant={suspending}
          busy={busyId === suspending.id}
          onCancel={() => setSuspending(null)}
          onSubmit={(reason) =>
            void act(suspending, async () => {
              await api.post(`/api/v1/tenants/${suspending.id}/suspend`, { reason });
              setSuspending(null);
            })
          }
        />
      )}

      {abonando !== null && (
        <AbonoDeSaldo tenant={abonando} onCancel={() => setAbonando(null)} />
      )}

      {asignandoTroncal !== null && (
        <Modal
          title={`Troncal de salida de «${asignandoTroncal.name}»`}
          description="Sin una troncal asignada la cuenta no puede originar. Los topes de arranque son bajos a propósito: se ajustan después en Límites."
          onClose={() => setAsignandoTroncal(null)}
        >
          <TrunkSelect
            trunks={trunks.data}
            error={trunks.error}
            disabled={busyId === asignandoTroncal.id}
            onChange={(trunkId) => {
              const tenant = asignandoTroncal;
              setAsignandoTroncal(null);
              void act(tenant, () =>
                api.post(`/api/v1/tenants/${tenant.id}/outbound-policies`, { trunk_id: trunkId }),
              );
            }}
          />
        </Modal>
      )}

      {asignandoDeck !== null && (
        <Modal
          title={`Tarifario de «${asignandoDeck.name}»`}
          description="Sin tarifario propio, la cuenta cobra con el deck por omisión."
          onClose={() => setAsignandoDeck(null)}
        >
          <DeckSelect
            decks={decks.data}
            error={decks.error}
            disabled={busyId === asignandoDeck.id}
            onChange={(rateDeckId) => {
              const tenant = asignandoDeck;
              setAsignandoDeck(null);
              void act(tenant, () =>
                api.put(`/api/v1/tenants/${tenant.id}/rate-deck`, { rate_deck_id: rateDeckId }),
              );
            }}
          />
        </Modal>
      )}

      {dandoNumeros !== null && (
        <NumerosDeLaCuenta tenant={dandoNumeros} onClose={() => setDandoNumeros(null)} />
      )}

      {dandoCallerID !== null && (
        <AltaDeCallerID
          tenant={dandoCallerID}
          onCancel={() => setDandoCallerID(null)}
          onCreated={() => setDandoCallerID(null)}
        />
      )}

      {dandoUsuario !== null && (
        <AltaDeUsuario
          tenant={dandoUsuario}
          onCancel={() => setDandoUsuario(null)}
        />
      )}

      {erasing !== null && (
        <EraseForm
          tenant={erasing}
          onCancel={() => setErasing(null)}
          onDone={() => {
            setErasing(null);
            void tenants.reload(true);
          }}
        />
      )}

      <DataTable<Tenant>
        data={tenants.data}
        columns={columnas}
        getRowId={(tenant) => tenant.id}
        loading={tenants.loading}
        error={tenants.error}
        onRetry={() => void tenants.reload(false)}
        caption="Cuentas de la plataforma"
        empty={{
          title: "Sin cuentas",
          description: "Ni la cuenta propia: algo salió mal al cargar.",
        }}
        search={{ placeholder: "Buscar por nombre o slug…" }}
        filters={[
          {
            columnId: "status",
            label: "Estado",
            options: [
              { value: "active", label: "Activa" },
              { value: "suspended", label: "Suspendida" },
              { value: "closed", label: "Cerrada" },
            ],
          },
          {
            columnId: "kill_switch",
            label: "Originación",
            options: [
              { value: "false", label: "Permitida" },
              { value: "true", label: "Cortada" },
            ],
          },
        ]}
        rowActions={acciones}
        hideableColumns
        urlStateKey="cuentas"
        pagination={{
          pageSize: tenants.enEstaPagina,
          hasNext: tenants.hayMas,
          hasPrevious: tenants.hayAnterior,
          onNext: tenants.siguiente,
          onPrevious: tenants.anterior,
        }}
      />
    </div>
  );
}

const TENANT_STATUS_LABELS: Record<Tenant["status"], string> = {
  active: "Activa",
  suspended: "Suspendida",
  closed: "Cerrada",
};

const TENANT_STATUS_TONES: Record<Tenant["status"], "neutral" | "good" | "warn" | "bad"> = {
  active: "good",
  suspended: "warn",
  closed: "neutral",
};

/**
 * DeckSelect asigna un rate deck a la cuenta.
 *
 * Arranca sin selección y no muestra el deck vigente porque el endpoint de
 * cuentas no lo devuelve. Mostrar un valor adivinado aquí sería peor que no
 * mostrar ninguno: es el dato del que depende lo que se cobra.
 */
/**
 * TrunkSelect da a la cuenta la troncal por la que puede marcar.
 *
 * Crea la política de salida con topes de arranque bajos —2 CPS, 10
 * simultáneas, 10 minutos, solo nacional—, que el administrador de la cuenta
 * ajusta después en Límites. Empezar generoso y corregir luego es como se
 * acaba pagando una factura que nadie vio venir.
 */
function TrunkSelect({
  trunks,
  error,
  disabled,
  onChange,
}: {
  trunks: Trunk[] | null;
  error: string | null;
  disabled: boolean;
  onChange: (trunkId: string) => void;
}) {
  if (error !== null) {
    return <span className="text-xs text-rose-300">Troncales no disponibles: {error}</span>;
  }
  if (trunks === null) return <span className="text-xs text-slate-600">Cargando troncales…</span>;

  const activas = trunks.filter((t) => t.status === "active" && t.direction !== "inbound");
  if (activas.length === 0) {
    return (
      <span className="text-xs text-slate-500">
        No hay troncales de salida activas que asignar.
      </span>
    );
  }

  return (
    <label className="flex items-center gap-2 text-xs text-slate-500">
      Asignar troncal de salida
      <select
        className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-slate-200 transition focus:border-sky-400/60 focus:outline-none [&>option]:bg-[#11162a]"
        disabled={disabled}
        defaultValue=""
        onChange={(e) => {
          if (e.target.value !== "") onChange(e.target.value);
          e.target.value = "";
        }}
      >
        <option value="">elegir…</option>
        {activas.map((trunk) => (
          <option key={trunk.id} value={trunk.id}>
            {trunk.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function DeckSelect({
  decks,
  error,
  disabled,
  onChange,
}: {
  decks: RateDeck[] | null;
  error: string | null;
  disabled: boolean;
  onChange: (rateDeckId: string) => void;
}) {
  if (error !== null) {
    return <span className="text-xs text-rose-300">Tarifas no disponibles: {error}</span>;
  }
  if (decks === null) return <span className="text-xs text-slate-600">Cargando tarifas…</span>;

  return (
    <label className="flex items-center gap-2 text-xs text-slate-500">
      Asignar tarifario
      <select
        className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-slate-200 transition focus:border-sky-400/60 focus:outline-none [&>option]:bg-[#11162a]"
        disabled={disabled}
        defaultValue=""
        onChange={(e) => {
          if (e.target.value !== "") onChange(e.target.value);
          e.target.value = "";
        }}
      >
        <option value="">elegir…</option>
        {decks.map((deck) => (
          <option key={deck.id} value={deck.id}>
            {deck.name}
            {deck.is_default ? " (por omisión)" : ""}
          </option>
        ))}
      </select>
    </label>
  );
}

function CreateTenantForm({ onCancel, onCreated }: { onCancel: () => void; onCreated: () => void }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/api/v1/tenants", { name, slug });
      onCreated();
    } catch (err) {
      setError(failureMessage(err, "no se pudo crear la cuenta"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Nueva cuenta"
      description="El slug identifica la cuenta en la API y no se puede cambiar."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field label="Nombre" hint="Como se llama el cliente. Máximo 120 caracteres.">
          <input
            className={inputClass}
            required
            maxLength={120}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // El slug se sugiere a partir del nombre, pero queda editable:
              // forma parte de la confirmación del borrado de datos, así que
              // tiene que ser algo que una persona reconozca y pueda teclear.
              if (slug === "") return;
            }}
          />
        </Field>

        <Field
          label="Slug"
          hint="Minúsculas, números y guiones, de 3 a 40 caracteres. Se usa para confirmar operaciones destructivas, así que conviene que sea reconocible."
        >
          <input
            className={`${inputClass} font-mono`}
            required
            pattern="[a-z0-9-]{3,40}"
            placeholder="seekop"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
          />
        </Field>

        {error !== null && <p className="text-sm text-rose-300">{error}</p>}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creando…" : "Crear cuenta"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SuspendForm({
  tenant,
  busy,
  onCancel,
  onSubmit,
}: {
  tenant: Tenant;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");

  return (
    <Modal
      title={`Suspender «${tenant.name}»`}
      onClose={onCancel}
    >
      <p className="text-sm font-semibold text-amber-200">Suspender «{tenant.name}»</p>
      <p className="mt-1 text-sm text-amber-200">
        Se cuelgan las llamadas en curso de la cuenta y no se admiten nuevas. El motivo queda en la
        bitácora de auditoría y se muestra en esta lista. Reanudar lo revierte.
      </p>
      <div className="mt-3 max-w-lg">
        <Field label="Motivo" hint="Lo lee quien tenga que explicar el corte después.">
          <input
            className={inputClass}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Saldo vencido a 30 días"
          />
        </Field>
      </div>
      <div className="mt-4 flex gap-2">
        <Button variant="danger" disabled={busy || reason.trim() === ""} onClick={() => onSubmit(reason)}>
          {busy ? "Suspendiendo…" : "Suspender"}
        </Button>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
      </div>
    </Modal>
  );
}

/**
 * EraseForm borra los datos de una cuenta.
 *
 * Pide el slug tecleado y un motivo porque el backend los exige, y los exige
 * por una razón: es la única operación del portal que destruye datos sin vuelta
 * atrás. Al terminar se muestra qué se borró y qué se conservó, con el conteo
 * que devuelve el servidor — no un "listo" que nadie puede verificar.
 */
function EraseForm({
  tenant,
  onCancel,
  onDone,
}: {
  tenant: Tenant;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [confirmSlug, setConfirmSlug] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [partial, setPartial] = useState<Record<string, number> | null>(null);
  const [result, setResult] = useState<ErasureResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPartial(null);
    setSubmitting(true);
    try {
      setResult(
        await api.delete<ErasureResult>(`/api/v1/tenants/${tenant.id}/data`, {
          confirm_slug: confirmSlug,
          reason,
        }),
      );
      onDone();
    } catch (err) {
      setError(failureMessage(err, "no se pudo borrar"));
      // Un borrado a medias responde ERASURE_INCOMPLETE con el conteo de lo que
      // sí se fue. Hay que mostrarlo: lo borrado no vuelve, y quien reintente
      // necesita saber por dónde iba.
      if (err instanceof ApiFailure && err.error.code === "ERASURE_INCOMPLETE") {
        const deleted = err.error.details?.["deleted"];
        if (deleted !== null && typeof deleted === "object") {
          setPartial(deleted as Record<string, number>);
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (result !== null) {
    return (
      <Modal
        title="Datos borrados"
        onClose={onCancel}
      >
        <p className="text-sm font-semibold text-slate-100">
          Datos de «{tenant.name}» borrados
        </p>
        <DeletedCounts counts={result.deleted} />
        <p className="mt-3 text-sm font-medium text-slate-300">Se conservó:</p>
        <ul className="mt-1 list-disc pl-5 text-sm text-slate-400">
          {result.kept.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="mt-4">
          <Button variant="secondary" onClick={onCancel}>
            Cerrar
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Borrar los datos de «${tenant.name}»`}
      onClose={onCancel}
    >
      <p className="text-sm font-semibold text-rose-200">Borrar los datos de «{tenant.name}»</p>
      <p className="mt-1 text-sm text-rose-200">
        Se destruyen CDR, intentos, eventos y metadatos de streams de esta cuenta. No hay vuelta
        atrás ni respaldo que el portal pueda restaurar. Se conservan los movimientos de saldo y la
        bitácora de auditoría, que son registro contable.
      </p>

      <form className="mt-4 space-y-4" onSubmit={onSubmit}>
        <div className="max-w-sm">
          <Field label={`Escribe ${tenant.slug} para confirmar`}>
            <input
              className={`${inputClass} font-mono`}
              required
              autoComplete="off"
              value={confirmSlug}
              onChange={(e) => setConfirmSlug(e.target.value)}
            />
          </Field>
        </div>

        <div className="max-w-lg">
          <Field label="Motivo" hint="Queda en la bitácora. Mínimo 3 caracteres.">
            <input
              className={inputClass}
              required
              minLength={3}
              placeholder="Solicitud de borrado del titular, folio 1234"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
        </div>

        {error !== null && <p className="text-sm font-medium text-rose-200">{error}</p>}
        {partial !== null && (
          <div className="rounded border border-rose-500/40 bg-white/[0.03] p-3">
            <p className="text-sm font-medium text-rose-200">
              El borrado quedó incompleto. Esto sí se borró:
            </p>
            <DeletedCounts counts={partial} />
          </div>
        )}

        <div className="flex gap-2">
          <Button type="submit" variant="danger" disabled={submitting || confirmSlug !== tenant.slug}>
            {submitting ? "Borrando…" : "Borrar los datos"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DeletedCounts({ counts }: { counts: Record<string, number> }) {
  const rows = Object.entries(counts).filter(([, n]) => n > 0);
  if (rows.length === 0) {
    return <p className="mt-2 text-sm text-slate-400">No había filas que borrar.</p>;
  }
  return (
    <ul className="mt-2 space-y-0.5 text-sm text-slate-300">
      {rows.map(([table, n]) => (
        <li key={table}>
          <code className="text-xs">{table}</code>: {n.toLocaleString("es-MX")}
        </li>
      ))}
    </ul>
  );
}

/**
 * AltaDeUsuario crea el primer usuario de una cuenta.
 *
 * La contraseña que devuelve el servidor se muestra **una sola vez** y no se
 * cierra sola: es lo único con lo que esa persona puede entrar, y la
 * plataforma no la guarda en claro.
 */
function AltaDeUsuario({ tenant, onCancel }: { tenant: Tenant; onCancel: () => void }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState<RoleKey>("tenant_admin");
  const [creado, setCreado] = useState<TenantUserCreated | null>(null);
  const [copiado, setCopiado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      setCreado(
        await api.post<TenantUserCreated>(`/api/v1/tenants/${tenant.id}/users`, {
          email,
          full_name: fullName,
          role,
        }),
      );
    } catch (err) {
      setError(failureMessage(err, "no se pudo crear el usuario"));
    } finally {
      setSubmitting(false);
    }
  }

  if (creado !== null) {
    const clave = creado.temporary_password;
    return (
      <Modal
        title="Usuario creado"
        onClose={onCancel}
      >
        <p className="text-sm font-semibold text-emerald-200">
          {clave === undefined
            ? `${creado.email} ya existía en la plataforma y se añadió a «${tenant.name}».`
            : `Usuario creado en «${tenant.name}». Esta contraseña no se vuelve a mostrar.`}
        </p>
        {clave === undefined ? (
          <p className="mt-1.5 text-sm text-emerald-100/80">
            Entra con la contraseña que ya usaba: no se le cambió, porque la necesita para las
            otras cuentas donde opera.
          </p>
        ) : (
          <>
            <p className="mt-1.5 text-sm text-emerald-100/80">
              Dásela por un canal seguro. La plataforma la guarda solo hasheada; si se pierde, hay
              que crear otro usuario.
            </p>
            <code className="mt-3 block overflow-x-auto rounded border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 font-mono text-sm text-slate-100">
              {clave}
            </code>
          </>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {clave !== undefined && (
            <Button
              variant="secondary"
              onClick={() => {
                navigator.clipboard
                  .writeText(`${creado.email}\n${clave}`)
                  .then(() => setCopiado(true))
                  .catch(() => setCopiado(false));
              }}
            >
              {copiado ? "Copiados" : "Copiar correo y contraseña"}
            </Button>
          )}
          <Button onClick={onCancel}>Listo</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={`Añadir un usuario a «${tenant.name}»`}
      description="Nace con contraseña temporal; se muestra una sola vez."
      onClose={onCancel}
    >
      <p className="mb-4 text-sm font-medium text-slate-100">
        Nuevo usuario para «{tenant.name}»
      </p>
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label="Correo"
          hint="Si ya existe en la plataforma, se le añade esta cuenta y conserva su contraseña."
        >
          <input
            className={inputClass}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>

        <Field label="Nombre" hint="Opcional.">
          <input className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>

        <Field
          label="Rol"
          hint="El primero de una cuenta normalmente es administrador: es quien puede invitar al resto."
        >
          <select
            className={inputClass}
            value={role}
            onChange={(e) => setRole(e.target.value as RoleKey)}
          >
            <option value="tenant_admin">Administrador de cuenta</option>
            <option value="developer">Desarrollador</option>
            <option value="billing_viewer">Facturación</option>
            <option value="read_only">Solo lectura</option>
          </select>
        </Field>

        {error !== null && <ActionError message={error} />}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "Creando…" : "Crear usuario"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * AbonoDeSaldo acredita saldo a una cuenta.
 *
 * El monto se escribe en pesos y se convierte a micro-MXN al mandarlo: la
 * plataforma guarda enteros en micro-unidades porque en centavos un segundo de
 * llamada nacional cuesta 0.58, y redondear eso por llamada se convierte en la
 * factura entera. Lo que no se hace es pedirle al operador que escriba
 * 500000000.
 */
function AbonoDeSaldo({ tenant, onCancel }: { tenant: Tenant; onCancel: () => void }) {
  const saldo = useResource<Balance>(`/api/v1/tenants/${tenant.id}/balance`);
  const [pesos, setPesos] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Se parsea con enteros y no con `Number` sobre el total: 0.1 + 0.2 en coma
  // flotante no es 0.3, y esto es dinero.
  const micros = aMicros(pesos);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (micros === null) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/api/v1/tenants/${tenant.id}/credits`, {
        amount_micros: micros,
        reason: motivo,
      });
      await saldo.reload(true);
      setPesos("");
      setMotivo("");
    } catch (err) {
      setError(failureMessage(err, "no se pudo abonar"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Abonar saldo a «${tenant.name}»`}
      description="El abono queda en el libro mayor y no se puede deshacer."
      onClose={onCancel}
    >
      <p className="mb-1 text-sm font-medium text-slate-100">Saldo de «{tenant.name}»</p>
      <p className="mb-4 text-sm text-slate-400">
        {saldo.loading
          ? "Consultando…"
          : saldo.error !== null
            ? `No se pudo consultar: ${saldo.error}`
            : `Disponible: ${saldo.data?.balance ?? "—"} ${saldo.data?.currency ?? ""}`}
        {" · "}
        Sin saldo, cada llamada se rechaza con «saldo insuficiente».
      </p>

      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Monto en pesos" hint="Hasta dos decimales.">
            <input
              className={inputClass}
              required
              inputMode="decimal"
              placeholder="500.00"
              value={pesos}
              onChange={(e) => setPesos(e.target.value)}
            />
          </Field>
          <Field label="Motivo" hint="Queda en el libro mayor y en la bitácora. Mínimo 3 caracteres.">
            <input
              className={inputClass}
              required
              minLength={3}
              placeholder="Fondeo inicial"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </Field>
        </div>

        {pesos !== "" && micros === null && (
          <p className="text-xs text-rose-300">
            El monto tiene que ser un número positivo con hasta dos decimales.
          </p>
        )}
        {error !== null && <ActionError message={error} />}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={submitting || micros === null || motivo.trim().length < 3}>
            {submitting ? "Abonando…" : "Abonar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cerrar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * aMicros convierte «500.25» a 500250000 sin pasar por coma flotante.
 *
 * `Math.round(Number(x) * 1e6)` funciona casi siempre, y «casi siempre» en
 * dinero es un error contable esperando turno. Devuelve `null` si no es un
 * monto válido.
 */
function aMicros(texto: string): number | null {
  const limpio = texto.trim().replace(/,/g, "");
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(limpio);
  if (m === null) return null;
  const enteros = Number(m[1]);
  const centavos = Number((m[2] ?? "").padEnd(2, "0"));
  const micros = enteros * 1_000_000 + centavos * 10_000;
  return micros > 0 ? micros : null;
}

/**
 * AltaDeCallerID registra un número presentador en una cuenta.
 *
 * Marcarlo verificado es decir que la titularidad consta por otra vía: el
 * contrato con el cliente, normalmente. Presentarse con el número de otro es
 * suplantación, así que la casilla lo dice con esas palabras y la aprobación
 * queda en el audit log a nombre de quien la hace.
 */
function AltaDeCallerID({
  tenant,
  onCancel,
  onCreated,
}: {
  tenant: Tenant;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [e164, setE164] = useState("");
  const [verificado, setVerificado] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [listo, setListo] = useState<string | null>(null);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const creado = await api.post<CallerId>(`/api/v1/tenants/${tenant.id}/caller-ids`, {
        e164: e164.trim(),
        verified: verificado,
      });
      setListo(creado.status);
    } catch (err) {
      setError(failureMessage(err, "no se pudo registrar el caller ID"));
    } finally {
      setSubmitting(false);
    }
  }

  if (listo !== null) {
    return (
      <Modal title="Caller ID registrado" onClose={onCreated}>
        <p className="text-sm text-slate-300">
          <strong className="text-slate-100">{e164}</strong> quedó registrado en «{tenant.name}»
          {listo === "verified"
            ? " y verificado: la cuenta ya puede presentarse con él."
            : " pendiente de verificación: la cuenta tendrá que completarla con la llamada."}
        </p>
        <div className="mt-4 flex justify-end">
          <Button onClick={onCreated}>Listo</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Añadir un caller ID a «${tenant.name}»`} onClose={onCancel}>
      <form className="space-y-4" onSubmit={onSubmit}>
        <Field
          label="Número en E.164"
          hint="Con el código de país. Es el número que verá el abonado al recibir la llamada."
        >
          <input
            className={inputClass}
            required
            placeholder="+525512345678"
            value={e164}
            onChange={(e) => setE164(e.target.value)}
          />
        </Field>

        <label className="flex items-start gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            className="mt-1"
            checked={verificado}
            onChange={(e) => setVerificado(e.target.checked)}
          />
          <span>
            Darlo por verificado
            <span className="mt-0.5 block text-xs text-slate-500">
              Sin la llamada de verificación. Hazlo solo si la titularidad del número te consta
              por contrato: presentarse con el número de otro es suplantación, y la aprobación
              queda registrada a tu nombre. Si lo dejas sin marcar, el cliente completará la
              verificación desde su portal.
            </span>
          </span>
        </label>

        {error !== null && <ActionError message={error} />}

        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || e164.trim() === ""}>
            {submitting ? "Registrando…" : "Registrar"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * NumerosDeLaCuenta reparte DID desde la ficha del cliente.
 *
 * Enseña dos listas porque son las dos preguntas que uno tiene delante: qué
 * números tiene ya este cliente, y cuáles quedan libres para darle.
 *
 * Quitar un número **borra su ruta entrante**, que apunta al servidor de este
 * cliente. Se avisa antes y no después: recuperarla es rehacerla a mano, y
 * mientras tanto las llamadas a ese número no contestan.
 */
function NumerosDeLaCuenta({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  // El inventario completo, con cuenta activa o sin ella: `scope=platform`.
  const inventario = useResource<PhoneNumber[]>("/api/v1/numbers?scope=platform&limit=200");
  // Las troncales hacen falta para el alta: un número entra por una, y cuál
  // es no se puede adivinar.
  const troncales = useResource<Trunk[]>("/api/v1/trunks");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nuevoE164, setNuevoE164] = useState("");
  const [nuevaTroncal, setNuevaTroncal] = useState("");
  const [altaEnCurso, setAltaEnCurso] = useState(false);

  async function darDeAlta(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setAltaEnCurso(true);
    try {
      // Nace ya asignado a esta cuenta: es el flujo normal —se compra un DID
      // para un cliente concreto— y hacerlo en dos pasos solo añade un estado
      // intermedio en el que el número no es de nadie.
      await api.post("/api/v1/numbers", {
        e164: nuevoE164.trim(),
        trunk_id: nuevaTroncal,
        tenant_id: tenant.id,
      });
      setNuevoE164("");
      await inventario.reload();
    } catch (err) {
      setError(failureMessage(err, "no se pudo dar de alta el número"));
    } finally {
      setAltaEnCurso(false);
    }
  }

  async function mover(numero: PhoneNumber, destino: string | null) {
    setError(null);
    setBusy(numero.id);
    try {
      await api.put(`/api/v1/numbers/${numero.id}/assignment`, { tenant_id: destino });
      await inventario.reload();
    } catch (err) {
      setError(failureMessage(err, "no se pudo cambiar la asignación"));
    } finally {
      setBusy(null);
    }
  }

  const numeros = inventario.data ?? [];
  const suyos = numeros.filter((n) => n.tenant_id === tenant.id);
  const libres = numeros.filter((n) => n.tenant_id === null);
  const deOtros = numeros.length - suyos.length - libres.length;

  return (
    <Modal title={`Números de «${tenant.name}»`} onClose={onClose}>
      {error !== null && <ActionError message={error} />}

      {inventario.error !== null ? (
        <ActionError message={inventario.error} />
      ) : inventario.data === null ? (
        <p className="text-sm text-slate-500">Cargando el inventario…</p>
      ) : (
        <div className="space-y-5">
          <section>
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Asignados a esta cuenta
            </h3>
            {suyos.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">
                Ninguno. Sin un DID asignado, la cuenta no puede recibir llamadas.
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {suyos.map((numero) => (
                  <li key={numero.id} className="flex items-center justify-between gap-3">
                    <span className="font-mono text-sm text-slate-100">{numero.e164}</span>
                    <Button
                      variant="secondary"
                      disabled={busy === numero.id}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Quitar ${numero.e164} de «${tenant.name}».\n\n` +
                              "Vuelve al inventario y su ruta entrante se borra: apunta al " +
                              "servidor de este cliente.",
                          )
                        ) {
                          void mover(numero, null);
                        }
                      }}
                    >
                      Quitar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Libres en el inventario
            </h3>
            {libres.length === 0 ? (
              <p className="mt-2 text-sm text-slate-500">
                No hay números sin dueño. Se dan de alta en Números, con la troncal por la que
                entran.
              </p>
            ) : (
              <ul className="mt-2 space-y-1.5">
                {libres.map((numero) => (
                  <li key={numero.id} className="flex items-center justify-between gap-3">
                    <span className="font-mono text-sm text-slate-100">{numero.e164}</span>
                    <Button
                      disabled={busy === numero.id}
                      onClick={() => void mover(numero, tenant.id)}
                    >
                      Asignar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-md border border-white/10 bg-white/[0.02] p-3">
            <h3 className="text-xs font-medium uppercase tracking-wide text-slate-400">
              Dar de alta un número para esta cuenta
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              El DID que compraste al carrier. Nace asignado a «{tenant.name}».
            </p>
            <form className="mt-3 space-y-3" onSubmit={darDeAlta}>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Número en E.164">
                  <input
                    className={inputClass}
                    required
                    placeholder="+525512345678"
                    value={nuevoE164}
                    onChange={(e) => setNuevoE164(e.target.value)}
                  />
                </Field>
                <Field label="Troncal por la que entra">
                  <select
                    className={inputClass}
                    required
                    value={nuevaTroncal}
                    onChange={(e) => setNuevaTroncal(e.target.value)}
                  >
                    <option value="">elegir…</option>
                    {(troncales.data ?? [])
                      .filter((t) => t.status === "active" && t.direction !== "outbound")
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>
                </Field>
              </div>
              <Button
                type="submit"
                disabled={altaEnCurso || nuevoE164.trim() === "" || nuevaTroncal === ""}
              >
                {altaEnCurso ? "Dando de alta…" : "Dar de alta y asignar"}
              </Button>
            </form>
          </section>

          {deOtros > 0 && (
            <p className="text-xs text-slate-500">
              Hay {deOtros} {deOtros === 1 ? "número asignado a otra cuenta" : "números asignados a otras cuentas"}.
              Para moverlos, se quitan primero desde la ficha de su cuenta: así queda claro a quién
              se le está retirando el número.
            </p>
          )}
        </div>
      )}

      <div className="mt-5 flex justify-end">
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>
    </Modal>
  );
}
